import React from 'react';
import InputBase from '@mui/material/InputBase';
import Typography from '@mui/material/Typography';
import FormHelperText from '@mui/material/FormHelperText';
import InputAdornment from '@mui/material/InputAdornment';
import FormControl from '@mui/material/FormControl';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import { withStyles } from '@mui/styles';
import noop from 'lodash/noop';
import useToggle from 'hooks/useToggle';
import IconButton from 'components/IconButton';
import FormLabel from './FormLabel';

export const StyledInput = withStyles((theme) => ({
  root: {
    'label + &': {
      marginTop: theme.spacing(0.5),
    },
    '& .MuiButton-contained': {
      borderRadius: 12,
    },
    '&.MuiInputBase-multiline': {
      padding: 0,
    },
  },
  input: {
    'height': 'auto',
    'borderRadius': 12,
    'position': 'relative',
    'zIndex': 0,
    'backgroundColor': theme.palette.background.paper,
    'border': '1px solid',
    'borderColor': ({ error }) =>
      error ? theme.palette.error.main : theme.palette.grey[300],
    'padding': '8px 16px',
    'transition': theme.transitions.create(['border-color']),
    '&:hover': {
      borderColor: ({ error }) =>
        error ? theme.palette.error.main : theme.palette.primary.lighter,
    },
    '&:focus': {
      borderColor: ({ error }) =>
        error ? theme.palette.error.main : theme.palette.primary.light,
    },
    '&:disabled': {
      backgroundColor: theme.palette.action.disabledBackground,
    },
  },
  inputMultiline: {
    borderRadius: theme.spacing(1),
  },
  inputSizeSmall: {
    padding: '8px 12px',
    borderRadius: 8,
  },
  inputAdornedEnd: {
    paddingRight: theme.spacing(4.5),
  },
  inputAdornedStart: {
    paddingLeft: theme.spacing(4.5),
  },
}))(InputBase);

const Input = React.forwardRef(
  (
    {
      id,
      name,
      label,
      tooltip,
      hint,
      placeholder,
      hasError,
      helpText,
      onChange = noop,
      value,
      onClick,
      onBlur,
      endAdornment,
      startAdornment,
      type,
      inputProps = {},
      multiline,
      rows,
      readOnly,
      disabled,
      ...other
    },
    ref,
  ) => {
    const [showPassword, toggleShowPassword] = useToggle(false);

    const handleChange = (e) => {
      if (type === 'number') {
        const { value, name } = e.target;
        if (value) {
          onChange({
            target: {
              name,
              value: value.replace(/(?!^)-|[^0-9.-]/g, '').match(/^-?\d*\.?\d*$/)?.[0] || '',
            },
          });
        } else {
          onChange(e);
        }
      } else {
        onChange(e);
      }
    };

    const isPassword = type === 'password';
    if (isPassword) {
      inputProps = {
        ...inputProps,
        type: showPassword ? 'text' : 'password',
      };
      endAdornment = (
        <IconButton
          onClick={toggleShowPassword}
          size="small"
          icon={
            showPassword ? (
              <VisibilityOffOutlinedIcon />
            ) : (
              <VisibilityOutlinedIcon />
            )
          }
        />
      );
    }

    return (
      <FormControl
        variant="standard"
        margin="normal"
        error={hasError}
        {...other}
      >
        {label && (
          <FormLabel label={label} tooltip={tooltip}>
            {label}
          </FormLabel>
        )}
        <StyledInput
          id={id}
          name={name}
          type={type === 'number' ? 'text' : type}
          inputRef={ref}
          placeholder={placeholder}
          value={value}
          onChange={handleChange}
          multiline={multiline}
          disabled={disabled}
          readOnly={readOnly}
          rows={rows}
          onBlur={onBlur}
          onClick={onClick}
          error={hasError}
          inputProps={inputProps}
          startAdornment={
            startAdornment && (
              <InputAdornment
                position="start"
                sx={{ position: 'absolute', left: 8, zIndex: 1 }}
              >
                {startAdornment}
              </InputAdornment>
            )
          }
          endAdornment={
            endAdornment && (
              <InputAdornment
                position="end"
                sx={{ position: 'absolute', right: 4 }}
              >
                {endAdornment}
              </InputAdornment>
            )
          }
          {...inputProps}
        />
        {hint && (
          <Typography
            component="p"
            variant="caption"
            color="textSecondary"
            sx={{ pl: 2, pt: 0.5 }}
          >
            {hint}
          </Typography>
        )}
        {hasError && <FormHelperText>{helpText}</FormHelperText>}
      </FormControl>
    );
  },
);

export default Input;
